import { useEffect, useState, useRef, useCallback } from "react"
import NotesEditor from "./invitees-component/NotesEditor"
import { Note, emptyNote } from "../conversations.type"
import { ReactComponent as CloseIcon } from "assets/images/icons/close-icon.svg"
import { getNoteMiddleware, updateNotesMiddleware } from "../conversations.api"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { remove } from "lodash"
import { formatDate } from "helpers/utils"
import { IUseDefaultValueProps, useBoolean } from "helpers/hooks"
import Button from "components/Button/Button"
import { Prompt } from "react-router-dom";

interface Props {
  activeConversationId: string
  dbName: string
  closeNote: () => void
  isSavingNote: IUseDefaultValueProps
}

const NoteColumn = (props: Props) => {
  const { activeConversationId, closeNote, isSavingNote, dbName } = props
  const [noteDetail, setNoteDetail] = useState<
    Note & { conversationId: string }
  >({ ...emptyNote, conversationId: "" })
  const [currentNoteContent, setCurrentNoteContent] = useState<string>("")
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const isLoadingDefault = useBoolean(false)
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!activeConversationId) {
      return
    }
    isLoadingDefault.setValue(false)
    setNoteDetail({ ...emptyNote, conversationId: "" })
    setCurrentNoteContent("")
    setHasUnsavedChanges(false)
    // Clear any existing auto-save interval
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current)
      autoSaveIntervalRef.current = null
    }
    getNote()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId])

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ""; // browser sẽ show confirm
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const getNote = () => {
    getNoteMiddleware(activeConversationId, dbName)
      .then((res) => {
        setNoteDetail({ ...res, conversationId: activeConversationId })
        setCurrentNoteContent(res.note || "")
        setHasUnsavedChanges(false)
      })
      .catch((error) => {
        toast(
          <LabelNotificationPage
            messenger={error.response?.data?.message}
            type="error"
          />
        )
      })
      .finally(() => {
        isLoadingDefault.setValue(true)
      })
  }

  // Auto-save function
  const performSave = useCallback(
    (note: string, showSuccessToast = false) => {
      if (!activeConversationId || !isLoadingDefault.value) return
      if (activeConversationId !== noteDetail.conversationId) return

      isSavingNote.setValue(true)
      let newNote = note
      noteDetail.files.forEach((file) => {
        newNote = newNote.replaceAll(file.url, file.file)
      })
      updateNotesMiddleware(activeConversationId, newNote, dbName)
        .then((_res) => {
          setNoteDetail({
            ...noteDetail,
            note,
            updated_at: new Date().toISOString(),
          })
          setCurrentNoteContent(note)
          setHasUnsavedChanges(false)
          if (showSuccessToast) {
            toast(
              <LabelNotificationPage
                messenger="Note saved successfully"
                type="success"
              />
            )
          }
        })
        .catch((error) => {
          toast(
            <LabelNotificationPage
              messenger={error.response?.data?.message}
              type="error"
            />
          )
        })
        .finally(() => {
          isSavingNote.setValue(false)
        })
    },
    [
      activeConversationId,
      noteDetail,
      isLoadingDefault.value,
      dbName,
      isSavingNote,
    ]
  )

  // Setup auto-save interval when there are unsaved changes
  useEffect(() => {
    if (hasUnsavedChanges && currentNoteContent !== undefined) {
      // Clear existing interval
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current)
      }
      // Set up new interval for auto-save every 5 seconds
      autoSaveIntervalRef.current = setInterval(() => {
        if (hasUnsavedChanges && currentNoteContent !== noteDetail.note) {
          performSave(currentNoteContent, false)
        }
      }, 5000)
    } else {
      // Clear interval when no unsaved changes
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current)
        autoSaveIntervalRef.current = null
      }
    }

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current)
      }
    }
  }, [hasUnsavedChanges, currentNoteContent, noteDetail.note, performSave])

  // Handle note content change
  const onNoteChange = (note: string) => {
    setCurrentNoteContent(note)
    const hasChanges = note !== noteDetail.note
    setHasUnsavedChanges(hasChanges)
  }

  // Manual save
  const handleManualSave = () => {
    if (hasUnsavedChanges && currentNoteContent !== undefined) {
      performSave(currentNoteContent, true)
    }
  }

  // Handle close with warning
  const handleClose = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        "You have unsaved changes. Unsaved changes will be lost. Leave?"
      )
      if (confirmed) {
        closeNote()
      }
    } else {
      closeNote()
    }
  }
  // Legacy onEditNotes - kept for compatibility but now uses performSave
  const onEditNotes = (note: string) => {
    performSave(note, false)
  }
  const onEditImageNote = (
    image: { file: string; file_name: string; url: string },
    type: "add" | "delete"
  ) => {
    if (!activeConversationId) return
    if (activeConversationId !== noteDetail.conversationId) return
    if (type === "add") {
      noteDetail.files.push(image)
    } else {
      remove(noteDetail.files, (item) => item.file === image.file)
    }
    noteDetail.updated_at = Date().toString()
    setNoteDetail({ ...noteDetail })
  }
  const onInsertAttachments = (
    attachments: { file: string; file_name: string; url: string }[]
  ) => {
    if (!activeConversationId) return
    if (activeConversationId !== noteDetail.conversationId) return
    isSavingNote.setValue(true)
    setNoteDetail({
      ...noteDetail,
      files: [...noteDetail.files, ...attachments],
      updated_at: Date().toString(),
    })
    isSavingNote.setValue(false)
  }
  return (
    <>
      <div className="flex flex-col flex-1 pl-6 pt-6 pb-6 pr-2 overflow-auto bg-white">
        <div className="flex items-center justify-between pb-3 min-h-32 overflow-hidden border-b border-gray-500">
          <p className="font-semibold text-16 leading-26 color-black-111111 ">
            Notes
          </p>
          <div className="flex gap-2 items-center">
            {hasUnsavedChanges && (
              <div
                style={{
                  background: "rgba(232, 122, 22,0.15)",
                  borderRadius: 6,
                  padding: "2px 4px",
                  maxHeight: 28,
                }}
              >
                <p className="font-normal text-13 leading-22 text-[#e87a16]">
                  Unsaved changes
                </p>
              </div>
            )}
            {!hasUnsavedChanges && noteDetail.note && (
              <p className="text-13 leading-22 font-normal">{`Saved at ${formatDate(
                noteDetail.updated_at,
                "DD MMM yyyy - HH:mm A"
              )}`}</p>
            )}
            <Button
              title="Save"
              sizeBtn="small"
              colorBtn="yellow"
              onClick={handleManualSave}
              disabled={!hasUnsavedChanges || isSavingNote.value}
              isDisabledBtn={!hasUnsavedChanges || isSavingNote.value}
              styleButton={{ marginRight: 8 }}
            />
            <CloseIcon
              className="hover-action-icon cursor-pointer"
              onClick={handleClose}
            />
          </div>
        </div>
        {isLoadingDefault.value && (
          <NotesEditor
            key={activeConversationId}
            noteDetail={noteDetail}
            onEditNotes={onNoteChange}
            activeConversationId={activeConversationId}
            onEditImage={onEditImageNote}
            onInsertAttachments={onInsertAttachments}
            isSavingNote={isSavingNote}
            dbKey={dbName}
          />
        )}
      </div>
      <Prompt
        when={hasUnsavedChanges}
        message={(location) => {
          const ok = window.confirm(
            "You have unsaved changes. Leave this page?"
          );

          if (ok) {
            closeNote()
          }

          return ok;
        }}
      />
    </>
  )
}

export default NoteColumn
